/* build 2026-08-26T-loopfix */
/**
 * HVM Cart — drawer orchestrator.
 * Defines <cart-drawer> (public contract expected by product-form.js,
 * product-lumin.js, quick-add.js, quick-add-bulk.js, quick-order-list.js):
 *   open(triggeredBy?), close(), setActiveElement(el),
 *   renderContents(parsedState), getSectionsToRender()
 *
 * Three rings:
 *   1. <cart-drawer> shell — owns open state, focus, toasts, uiState.
 *      Never re-rendered. All clicks are delegated from here, so the
 *      markup below it can be swapped wholesale by third parties.
 *   2. #CartDrawer — replaceable in full (quick-add-bulk does this).
 *   3. [data-hvm-region] containers — the patch unit for our own
 *      mutations, so scroll/focus/disclosures survive updates.
 */
(() => {
  if (customElements.get('cart-drawer')) return;

  const HVMCart = window.HVMCart;

  HVMCart.uiState = {
    openPanel: null,
    couponDraft: '',
    couponOpen: false,
    lastTierPct: null,
    checkoutInFlight: false,
    knownCodes: [],
  };

  class HvmCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.queue = new HVMCart.MutationQueue();
      this.debouncer = new HVMCart.LineDebouncer(300);
      this.activeElement = null;
      this._inertTargets = [];
      this._patching = false;
      this._onDocumentKeydown = this._onDocumentKeydown.bind(this);
    }

    connectedCallback() {
      this._readConfig();
      this.store = new HVMCart.Store(this.config);
      this.api = new HVMCart.CartAPI(window.routes, ['cart-drawer', 'cart-icon-bubble']);
      this._hydrateFromDOM();
      // Un carrito viejo puede traer un regalo que ya no es gratis (promo
      // terminada, app desinstalada): las reglas también corren al cargar,
      // no solo tras una mutación del propio drawer.
      this._enqueueRules();

      this.addEventListener('click', (event) => this._onDelegatedClick(event));
      this.addEventListener('hvm:line:set', (event) => {
        const { lineKey, quantity, immediate } = event.detail;
        this.setLineQuantity(lineKey, quantity, { immediate });
      });
      this.addEventListener('hvm:coupon:apply', (event) => this.applyDiscount(event.detail.code));

      this._bindHeaderIcon();
      this._observeExternalSwaps();
      this._bindDesignMode();

      // Custom scroll indicator: scroll doesn't bubble, so listen in capture.
      // Timer throttle, not rAF — rAF never fires while the page isn't
      // compositing (hidden tab, some webviews), same caveat as open().
      this._indicatorTimer = null;
      this.addEventListener(
        'scroll',
        (event) => {
          if (!event.target || !event.target.hasAttribute || !event.target.hasAttribute('data-hvm-scroll')) return;
          if (this._indicatorTimer) return;
          this._indicatorTimer = setTimeout(() => {
            this._indicatorTimer = null;
            this._updateScrollIndicator();
          }, 16);
        },
        true
      );
      this._onWindowResize = () => this._updateScrollIndicator();
      window.addEventListener('resize', this._onWindowResize);
      this._updateScrollIndicator();

      if (window.subscribe && window.PUB_SUB_EVENTS) {
        this._unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
          if (!payload || payload.source === 'hvm-cart-drawer' || payload.source === 'product-form') return;
          this._scheduleResync();
        });
      }

      if (this.classList.contains('active')) this._lockPage();
    }

    disconnectedCallback() {
      if (this._unsubscribe) this._unsubscribe();
      if (this._observer) this._observer.disconnect();
      document.removeEventListener('keydown', this._onDocumentKeydown);
      if (this._onWindowResize) window.removeEventListener('resize', this._onWindowResize);
    }

    /* ------------------------------------------------------------ *
     * Config / state hydration (embedded JSON, never DOM scraping)
     * ------------------------------------------------------------ */
    _readConfig() {
      const node = this.querySelector('[data-hvm-cart-config]');
      this.config = node ? JSON.parse(node.textContent) : { strings: {}, tiers: {}, promo: {} };
      this.strings = this.config.strings || {};
    }

    _hydrateFromDOM() {
      const node = this.querySelector('[data-hvm-cart-state]');
      if (!node) return null;
      try {
        const cart = JSON.parse(node.textContent);
        return this.store.commit(cart);
      } catch (err) {
        return null;
      }
    }

    /* ------------------------------------------------------------ *
     * Public contract
     * ------------------------------------------------------------ */
    getSectionsToRender() {
      return [
        { id: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble' },
      ];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    renderContents(parsedState) {
      if (parsedState && parsedState.sections) {
        this._applySections(parsedState.sections);
        this._afterExternalRender();
      }
      this.open();
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      if (this.classList.contains('active')) return;

      // Timer, not rAF: rAF never fires while the page isn't compositing
      // (hidden tab, some webviews) and the drawer would silently not open.
      // The macrotask delay still lets the initial style resolve so the CSS
      // transition runs.
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this._lockPage();
      document.addEventListener('keydown', this._onDocumentKeydown);
      document.dispatchEvent(new CustomEvent('cart:open'));
      document.dispatchEvent(new CustomEvent('hvm:cart:open'));

      const panel = this._panel();
      if (panel) {
        const focusTarget = panel.querySelector('[data-hvm-autofocus]') || panel;
        // Trap on the SHELL (this), not the panel: the toast container lives
        // outside the panel and its Deshacer button must stay reachable.
        // One-shot guard: the overlay's transitionend fires before the panel's
        // and the 500ms fallback covers prefers-reduced-motion (no transition).
        let trapped = false;
        const trap = () => {
          if (trapped) return;
          trapped = true;
          clearTimeout(trapTimer);
          if (typeof trapFocus === 'function') trapFocus(this, focusTarget);
        };
        this.addEventListener('transitionend', trap, { once: true });
        const trapTimer = setTimeout(trap, 500);
      }

      this._revalidateOnOpen();
      this._updateScrollIndicator();
    }

    close() {
      if (!this.classList.contains('active') && !this._pageLocked) return;
      this.classList.remove('active');
      document.removeEventListener('keydown', this._onDocumentKeydown);
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.activeElement || document.getElementById('cart-icon-bubble'));
      }
      this._unlockPage();
      document.dispatchEvent(new CustomEvent('hvm:cart:close'));
    }

    /* ------------------------------------------------------------ *
     * Page lock: scroll + inert background
     * ------------------------------------------------------------ */
    _lockPage() {
      if (this._pageLocked) return;
      this._pageLocked = true;
      const compensation = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--hvm-scrollbar-comp', `${compensation}px`);
      document.body.classList.add('hvm-scroll-locked', 'overflow-hidden');

      // `{% section %}` wraps <cart-drawer> in a .shopify-section div, so the
      // check must be "does not contain the drawer", not identity — otherwise
      // the drawer inerts itself through its own wrapper.
      // inert recalcula estilos de toda la página: se aplica cuando el panel
      // ya terminó de entrar, si no, la animación arranca tarde y a saltos.
      this._inertTargets = [];
      clearTimeout(this._inertTimer);
      this._inertTimer = setTimeout(() => {
        if (!this._pageLocked) return;
        this._inertTargets = [...document.body.children].filter(
          (child) => !child.contains(this) && !['SCRIPT', 'STYLE', 'LINK'].includes(child.tagName) && !child.inert
        );
        this._inertTargets.forEach((child) => {
          child.inert = true;
        });
      }, 520);
    }

    _unlockPage() {
      if (!this._pageLocked) return;
      this._pageLocked = false;
      clearTimeout(this._inertTimer);
      document.body.classList.remove('hvm-scroll-locked', 'overflow-hidden');
      this._inertTargets.forEach((child) => {
        child.inert = false;
      });
      this._inertTargets = [];
    }

    _onDocumentKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    /* ------------------------------------------------------------ *
     * Wiring
     * ------------------------------------------------------------ */
    _bindHeaderIcon() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink || cartLink.dataset.hvmBound) return;
      cartLink.dataset.hvmBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-controls', 'CartDrawer');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code && event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    _bindDesignMode() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target.contains(this) || this.closest('.shopify-section') === event.target) this.open();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target.contains(this) || this.closest('.shopify-section') === event.target) this.close();
      });
    }

    /** Third parties (quick-add-bulk, quick-order-list) replace the whole
     *  innerHTML of #CartDrawer without telling us. Observe and rehydrate. */
    _observeExternalSwaps() {
      const root = this.querySelector('#CartDrawer');
      if (!root) return;
      this._observer = new MutationObserver(() => {
        if (this._patching) return;
        this._afterExternalRender();
      });
      this._observer.observe(root, { childList: true });
    }

    _afterExternalRender() {
      const committed = this._hydrateFromDOM();
      this._syncEmptyClass();
      this._updateScrollIndicator();
      if (committed) {
        this._announceCart(committed.cart);
        this._enqueueRules();
      }
    }

    /* ------------------------------------------------------------ *
     * Delegated actions
     * ------------------------------------------------------------ */
    _onDelegatedClick(event) {
      const target = event.target.closest('[data-hvm-action]');
      if (!target || target.hasAttribute('disabled')) return;
      const action = target.dataset.hvmAction;
      this._lastActionFocus = target.dataset.hvmFocus || null;

      switch (action) {
        case 'close':
          event.preventDefault();
          this.close();
          break;
        case 'remove-line':
          event.preventDefault();
          this.removeLine(target.dataset.lineKey);
          break;
        case 'set-line':
          event.preventDefault();
          this.setLineQuantity(target.dataset.lineKey, parseInt(target.dataset.quantity, 10), {
            immediate: true,
            busyEl: target,
          });
          break;
        case 'add-gift':
          event.preventDefault();
          this.addGift(target.dataset.variantId, target.dataset.giftSource || 'tier', target.dataset.giftQuantity, target);
          break;
        case 'add-product':
          event.preventDefault();
          this.addProduct(target.dataset.variantId, target);
          break;
        case 'remove-coupon':
          event.preventDefault();
          this.removeDiscount(target.dataset.code);
          break;
        case 'checkout':
          this._onCheckout(event, target);
          break;
      }
    }

    /* ------------------------------------------------------------ *
     * Mutations
     * ------------------------------------------------------------ */
    setLineQuantity(lineKey, quantity, { immediate = false, busyEl = null } = {}) {
      if (!lineKey || !Number.isFinite(quantity) || quantity < 0) return;
      const run = () =>
        this.queue.enqueue(() => this._executeChange(lineKey, quantity, { busyEl }));
      if (immediate) {
        this.debouncer.cancel(lineKey);
        run();
      } else {
        this._setLinePending(lineKey, true);
        this.debouncer.schedule(lineKey, run);
      }
    }

    removeLine(lineKey) {
      const item = this.store.findItem(lineKey);
      this.debouncer.cancel(lineKey);
      this._setLinePending(lineKey, true);
      this.queue.enqueue(() => this._executeChange(lineKey, 0, { removedItem: item }));
    }

    /** Una sola línea con N unidades (promo "compra 3 y llévate 2"); la
     *  regla R3 la recorta si excede lo permitido para su fuente. */
    addGift(variantId, source, quantity, busyEl) {
      const qty = Math.max(1, Number(quantity) || 1);
      this._enqueueAdd([{ id: Number(variantId), quantity: qty, properties: { _hvm_gift: source } }], busyEl);
    }

    /* Regalo automático de la promo (ajuste "Agregar el regalo en automático").
       Corre cuando las reglas ya no tienen correcciones: si el carrito pinta el
       pill pendiente de la promo, lo agrega solo. Mientras el escalón ganado
       no cambie se intenta una sola vez, así un regalo que no queda en $0 (la
       regla lo quita) no entra en ciclo. */
    _autoAddPromoGift() {
      const promo = this.config.promo || {};
      if (!promo.active || !promo.autoAdd || !this.store.derived) return;
      // Bajar de escalón libera el intento: al volver a subir, el regalo entra otra vez.
      if (this._autoGiftTried && Number(this.store.derived.promoGiftQuantity) !== this._autoGiftTried) this._autoGiftTried = null;
      const pill = this.querySelector('[data-hvm-action="add-gift"][data-gift-source="promo"]');
      if (!pill || pill.hasAttribute('disabled')) return;
      const earned = Number(this.store.derived.promoGiftQuantity) || 0;
      if (!earned || this._autoGiftTried === earned) return;
      this._autoGiftTried = earned;
      this.addGift(pill.dataset.variantId, 'promo', pill.dataset.giftQuantity, null);
    }

    addProduct(variantId, busyEl) {
      this._enqueueAdd([{ id: Number(variantId), quantity: 1 }], busyEl);
    }

    /** /cart/add.js responds with the ADDED items, not the full cart: after
     *  patching the returned sections, the canonical state is rehydrated from
     *  the freshly rendered embedded JSON. */
    _enqueueAdd(items, busyEl) {
      if (!items[0] || !items[0].id) return;
      this._setBusy(busyEl, true);
      this.queue.enqueue(async () => {
        try {
          const response = await this.api.add(items);
          if (response.sections) this._applySections(response.sections);
          const committed = this._hydrateFromDOM();
          this._syncEmptyClass();
          if (committed) {
            if (window.publish && window.PUB_SUB_EVENTS) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hvm-cart-drawer', cartData: committed.cart });
            }
            document.dispatchEvent(
              new CustomEvent('hvm:cart:updated', { detail: { cart: committed.cart, diff: committed.diff } })
            );
            if (items[0].properties && items[0].properties._hvm_gift) {
              this._announce(this.strings.giftAdded || 'Regalo agregado al carrito');
            } else {
              this._announceCart(committed.cart);
            }
          }
          this._enqueueRules();
        } catch (error) {
          this._handleMutationError(error, null);
        } finally {
          this._setBusy(busyEl, false);
        }
      });
    }

    /** The embedded Liquid state only sees product-targeting code discounts
     *  (shipping codes have no discount_application entry), so the last full
     *  list returned by the Cart API is remembered and merged in — otherwise
     *  re-sending `discount` would silently drop a free-shipping code. */
    _knownDiscountCodes() {
      const merged = new Map();
      (HVMCart.uiState.knownCodes || []).forEach((entry) => merged.set(entry.code.toLowerCase(), entry));
      (((this.store.cart && this.store.cart.discount_codes) || [])).forEach((entry) =>
        merged.set(entry.code.toLowerCase(), entry)
      );
      return [...merged.values()];
    }

    applyDiscount(code) {
      if (!code) return;
      const existing = this._knownDiscountCodes()
        .filter((entry) => entry.applicable)
        .map((entry) => entry.code);
      if (!existing.some((entry) => entry.toLowerCase() === code.toLowerCase())) existing.push(code);
      const codes = existing.join(',');
      this.queue.enqueue(async () => {
        try {
          const response = await this.api.update({ discount: codes });
          if (response.discount_codes) HVMCart.uiState.knownCodes = response.discount_codes;
          const entry = (response.discount_codes || []).find(
            (candidate) => candidate.code.toLowerCase() === code.toLowerCase()
          );
          const applied = !!(entry && entry.applicable);
          if (applied) HVMCart.uiState.couponDraft = '';
          await this._commitResponse(response, { announce: null });
          if (applied) {
            this._announce(this.strings.couponApplied || 'Cupón aplicado');
          } else {
            this._setCouponError(this.strings.couponInvalid || 'Código no válido');
            this._announce(this.strings.couponInvalid || 'Código no válido');
          }
        } catch (error) {
          this._handleMutationError(error, null);
        } finally {
          const coupon = this.querySelector('hvm-coupon-disclosure');
          if (coupon && typeof coupon.resetBusy === 'function') coupon.resetBusy();
        }
      });
    }

    removeDiscount(code) {
      const remaining = this._knownDiscountCodes()
        .filter((entry) => entry.applicable && entry.code.toLowerCase() !== String(code).toLowerCase())
        .map((entry) => entry.code)
        .join(',');
      this.queue.enqueue(async () => {
        try {
          const response = await this.api.update({ discount: remaining });
          if (response.discount_codes) HVMCart.uiState.knownCodes = response.discount_codes;
          await this._commitResponse(response, { announce: null });
        } catch (error) {
          this._handleMutationError(error, null);
        }
      });
    }

    async _executeChange(lineKey, quantity, { removedItem = null, busyEl = null } = {}) {
      this._setBusy(busyEl, true);
      try {
        // Baseline BEFORE mutating. Compare VARIANT-level totals, never the
        // edited key alone: apps like Appstle Bundles legally split an edited
        // line into several lines (their tier buckets keep "3 + 1" instead of
        // one line of 4), so the edited key reads lower than requested even
        // with plenty of stock — that looked like a false stock clamp.
        const beforeItems = (this.store.cart && this.store.cart.items) || [];
        const beforeItem = beforeItems.find((item) => item.key === lineKey) || null;
        const variantId = beforeItem ? String(beforeItem.variant_id) : null;

        // Cuando el BXGY nativo reparte su descuento, Shopify separa el mismo
        // variant en varias líneas (keys distintos) que el drawer renderiza
        // fusionadas en una sola fila. Editar una sola de esas líneas con
        // change.js desbalancea el reparto de descuentos (el 50% o el regalo
        // pueden desaparecer del carrito): se vacían todas las líneas planas
        // del variant y se re-agrega la cantidad pedida como línea única.
        const isPlainLine = (item) =>
          !item.selling_plan_allocation &&
          (!item.properties || Object.keys(item.properties).length === 0);
        const siblings =
          beforeItem && isPlainLine(beforeItem)
            ? beforeItems.filter((item) => String(item.variant_id) === variantId && isPlainLine(item))
            : [];
        if (siblings.length > 1) {
          // La secuencia vaciar+re-agregar no es atómica: si una operación
          // intermedia falla (red, throttling), el carrito queda con menos
          // unidades de las pedidas. Un retry por operación reduce ese riesgo.
          const withRetry = async (op) => {
            try {
              return await op();
            } catch (err) {
              await new Promise((resolve) => setTimeout(resolve, 700));
              return op();
            }
          };
          // Vaciar UNA línea a la vez y releer los keys de cada respuesta:
          // al remover una porción, la reasignación del descuento puede
          // re-generar los keys de las restantes y un key pre-capturado
          // responde 400.
          let response = null;
          let currentItems = beforeItems;
          let guard = 0;
          while (guard < 8) {
            const target = currentItems.find(
              (item) => String(item.variant_id) === variantId && isPlainLine(item)
            );
            if (!target) break;
            response = await withRetry(() => this.api.changeByKey(target.key, 0));
            currentItems = (response && response.items) || [];
            guard += 1;
          }
          if (quantity > 0) {
            const added = await withRetry(() => this.api.add([{ id: beforeItem.variant_id, quantity }]));
            if (added.sections) this._applySections(added.sections);
            const committed = this._hydrateFromDOM();
            this._syncEmptyClass();
            if (committed) {
              if (window.publish && window.PUB_SUB_EVENTS) {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hvm-cart-drawer', cartData: committed.cart });
              }
              document.dispatchEvent(
                new CustomEvent('hvm:cart:updated', { detail: { cart: committed.cart, diff: committed.diff } })
              );
              this._announceCart(committed.cart);
            }
            this._enqueueRules();
          } else {
            await this._commitResponse(response, { announce: 'quantity', announceKey: lineKey });
            if (removedItem && !HVMCart.isGiftItem(removedItem)) {
              const mergedQty = siblings.reduce((sum, item) => sum + item.quantity, 0);
              this._offerUndo(Object.assign({}, removedItem, { quantity: mergedQty }));
            }
          }
          this._scheduleResync();
          return;
        }
        const beforeVariantQty = variantId
          ? beforeItems.reduce(
              (sum, item) => (String(item.variant_id) === variantId ? sum + item.quantity : sum),
              0
            )
          : 0;

        const response = await this.api.changeByKey(lineKey, quantity);

        let clamped = false;
        let availableForLine = 0;
        if (variantId !== null) {
          const afterVariantQty = (response.items || []).reduce(
            (sum, item) => (String(item.variant_id) === variantId ? sum + item.quantity : sum),
            0
          );
          const expectedVariantQty = beforeVariantQty - beforeItem.quantity + quantity;
          const otherLinesQty = expectedVariantQty - quantity;
          availableForLine = Math.max(afterVariantQty - otherLinesQty, 0);
          clamped = quantity > 0 && afterVariantQty < expectedVariantQty;
        } else {
          // Stale key (line not found before the change): legacy per-key check.
          const responseItem = (response.items || []).find((item) => item.key === lineKey);
          availableForLine = responseItem ? responseItem.quantity : 0;
          clamped = quantity > 0 && availableForLine < quantity;
        }

        await this._commitResponse(response, { announce: 'quantity', announceKey: lineKey });

        if (clamped) {
          const message = (this.strings.quantityClamped || 'Solo hay [n] disponibles').replace(
            '[n]',
            String(availableForLine)
          );
          this._setLineError(lineKey, message);
          this._announce(message);
        }

        if (quantity === 0 && removedItem && !HVMCart.isGiftItem(removedItem)) {
          this._offerUndo(removedItem);
        }
      } catch (error) {
        this._handleMutationError(error, lineKey);
      } finally {
        this._setBusy(busyEl, false);
      }
    }

    /* ------------------------------------------------------------ *
     * Commit + render + rules
     * ------------------------------------------------------------ */
    async _commitResponse(response, { announce = null, announceKey = null } = {}) {
      if (response.discount_codes) HVMCart.uiState.knownCodes = response.discount_codes;
      const committed = this.store.commit(response);
      if (response.sections) this._applySections(response.sections);
      this._syncEmptyClass();

      if (window.publish && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hvm-cart-drawer', cartData: response });
      }
      document.dispatchEvent(
        new CustomEvent('hvm:cart:updated', { detail: { cart: response, diff: committed.diff } })
      );

      if (announce === 'quantity' && announceKey) {
        const item = this.store.findItem(announceKey);
        if (item) {
          this._announce(
            (this.strings.quantityUpdated || 'Cantidad de [title]: [n]. Total: [total]')
              .replace('[title]', item.product_title)
              .replace('[n]', String(item.quantity))
              .replace('[total]', HVMCart.formatMoney(this.store.cart.total_price))
          );
        } else {
          this._announceCart(response);
        }
      } else if (announce) {
        this._announceCart(response);
      }

      this._enqueueRules();
      return committed;
    }

    _enqueueRules() {
      if (this._rulesScheduled) return;
      this._rulesScheduled = true;
      this.queue.enqueue(async () => {
        this._rulesScheduled = false;
        await this._runRules(0);
      });
    }

    /** Applies ONE correction per pass via change.js and re-evaluates.
     *  Line item keys are NOT stable (they change when discount applications
     *  change), and update.js has a history of matching the wrong line when
     *  two lines share a variant (paid Gummy + gift Gummy). change.js with a
     *  fresh key from the latest commit is the only safe primitive. */
    async _runRules(pass) {
      if (!this.store.derived) return;
      const corrections = HVMCart.evaluateRules(this.store.derived, this.config);
      if (!corrections.length) {
        this._autoAddPromoGift();
        return;
      }
      if (pass >= 4) {
        await this._resync();
        return;
      }

      const correction = corrections[0];
      try {
        const quantity = correction.type === 'remove' ? 0 : correction.quantity;
        const response = await this.api.changeByKey(correction.key, quantity);
        const committed = this.store.commit(response);
        if (response.sections) this._applySections(response.sections);
        this._syncEmptyClass();
        if (window.publish && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hvm-cart-drawer', cartData: response });
        }
        document.dispatchEvent(
          new CustomEvent('hvm:cart:updated', { detail: { cart: response, diff: committed.diff } })
        );
        this._toastCorrections([correction]);
        await this._runRules(pass + 1);
      } catch (error) {
        if (error && !error.isNetwork) {
          // Stale key (the line was re-keyed server-side): resync and retry once.
          await this._resync();
          if (pass < 4) await this._runRules(pass + 1);
        } else {
          this._handleMutationError(error, null);
        }
      }
    }

    _toastCorrections(corrections) {
      corrections.forEach((correction) => {
        const title = correction.item ? correction.item.product_title : '';
        if (correction.reason === 'tier_lost') {
          this._toast(
            (this.strings.giftLost || 'Quitamos [title] gratis, te faltan [missing] para recuperarlo')
              .replace('[title]', title)
              .replace('[missing]', HVMCart.formatMoney(correction.missing || 0))
          );
        } else if (correction.reason === 'promo_lost') {
          const n = Math.max(1, Number(correction.missingUnits) || 1);
          const unit = (this.config.promo && this.config.promo.unitNoun) || 'frasco';
          this._toast(
            (this.strings.giftLostUnits || 'Quitamos tu regalo ([title]), te falta[n_plural] [n] [unit] para recuperarlo')
              .replace('[title]', title)
              .replace('[n_plural]', n > 1 ? 'n' : '')
              .replace('[n]', String(n))
              .replace('[unit]', n > 1 ? unit + 's' : unit)
          );
        } else if (correction.reason === 'not_free') {
          this._toast(
            (this.strings.giftNotFree || 'No pudimos aplicar [title] como regalo').replace('[title]', title)
          );
        }
      });
    }

    /* ------------------------------------------------------------ *
     * Rendering
     * ------------------------------------------------------------ */
    _panel() {
      return this.querySelector('[data-hvm-panel]');
    }

    _applySections(sections) {
      this._beginPatch();
      if (sections['cart-drawer']) this._patchDrawer(sections['cart-drawer']);
      if (sections['cart-icon-bubble']) this._patchBubble(sections['cart-icon-bubble']);
      this._readConfig();
      this.store.config = this.config;
      // Region swaps detach the nodes the focus trap cached — re-trap on the
      // current active element so Tab keeps wrapping inside the dialog.
      if (this.classList.contains('active') && typeof trapFocus === 'function') {
        const active = this.contains(document.activeElement) ? document.activeElement : this._panel();
        if (active) trapFocus(this, active);
      }
    }

    /** MutationObserver callbacks fire at the microtask checkpoint, AFTER the
     *  synchronous patch returns — a plain boolean reset in `finally` would
     *  already be false by then and our own patches would look external.
     *  Resetting in a macrotask (setTimeout 0) runs after the observer. */
    _beginPatch() {
      this._patching = true;
      clearTimeout(this._patchingTimer);
      this._patchingTimer = setTimeout(() => {
        this._patching = false;
      }, 0);
    }

    _patchDrawer(html) {
      const incoming = new DOMParser().parseFromString(html, 'text/html');
      const incomingRoot = incoming.querySelector('#CartDrawer');
      const currentRoot = this.querySelector('#CartDrawer');
      if (!incomingRoot || !currentRoot) return;

      const snapshot = this._snapshotUI();

      const currentRegions = currentRoot.querySelectorAll('[data-hvm-region]');
      const incomingByName = {};
      incomingRoot.querySelectorAll('[data-hvm-region]').forEach((region) => {
        incomingByName[region.dataset.hvmRegion] = region;
      });

      const currentNames = [...currentRegions].map((region) => region.dataset.hvmRegion).sort().join(',');
      const incomingNames = Object.keys(incomingByName).sort().join(',');

      if (!currentRegions.length || currentNames !== incomingNames) {
        currentRoot.innerHTML = incomingRoot.innerHTML;
      } else {
        currentRegions.forEach((region) => {
          const next = incomingByName[region.dataset.hvmRegion];
          if (next && region.innerHTML !== next.innerHTML) {
            region.innerHTML = next.innerHTML;
          }
        });
      }

      this._restoreUI(snapshot);
      this._updateScrollIndicator();
    }

    _patchBubble(html) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      const incoming = new DOMParser().parseFromString(html, 'text/html');
      const inner = incoming.querySelector('.shopify-section');
      if (inner) bubble.innerHTML = inner.innerHTML;
    }

    _snapshotUI() {
      const scroller = this.querySelector('[data-hvm-scroll]');
      const active = document.activeElement;
      let focusToken =
        active && this.contains(active) && active.dataset ? active.dataset.hvmFocus || null : null;
      // Disabling a busy button drops focus to <body> before the patch runs;
      // the last activated control is the best restore target then.
      if (!focusToken && this._lastActionFocus) focusToken = this._lastActionFocus;
      return { scrollTop: scroller ? scroller.scrollTop : 0, focusToken };
    }

    _restoreUI(snapshot) {
      const scroller = this.querySelector('[data-hvm-scroll]');
      if (scroller) scroller.scrollTop = snapshot.scrollTop;
      if (!snapshot.focusToken) return;
      const target = this.querySelector(`[data-hvm-focus="${CSS.escape(snapshot.focusToken)}"]`);
      if (target && !target.disabled) {
        target.focus({ preventScroll: true });
      } else if (this.classList.contains('active')) {
        // The control the user activated no longer exists (gift added, upsell
        // card removed, upgrade row gone) — land on the dialog, not on <body>.
        const panel = this._panel();
        if (panel) panel.focus({ preventScroll: true });
      }
    }

    /* ------------------------------------------------------------ *
     * Scroll indicator: custom thumb over the scroller. Native bars
     * are hidden in CSS (iOS/macOS only show them mid-scroll); this
     * one is visible whenever the list overflows, as an affordance.
     * Lives on the panel (outside the regions), so region patches
     * keep it; a wholesale #CartDrawer swap recreates it lazily.
     * ------------------------------------------------------------ */
    _ensureScrollIndicator() {
      const panel = this._panel();
      if (!panel) return null;
      let bar = panel.querySelector('.hvm-cart__scrollbar');
      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'hvm-cart__scrollbar';
        bar.setAttribute('aria-hidden', 'true');
        const thumb = document.createElement('div');
        thumb.className = 'hvm-cart__scrollbar-thumb';
        bar.appendChild(thumb);
        panel.appendChild(bar);
      }
      return bar;
    }

    _updateScrollIndicator() {
      const bar = this._ensureScrollIndicator();
      const scroller = this.querySelector('[data-hvm-scroll]');
      if (!bar || !scroller) return;
      const overflow = scroller.scrollHeight - scroller.clientHeight;
      if (overflow <= 8) {
        bar.classList.remove('is-visible');
        return;
      }
      const panel = this._panel();
      // Relative offsets, so the panel's closed-state transform cancels out.
      const trackTop = scroller.getBoundingClientRect().top - panel.getBoundingClientRect().top + 6;
      const trackHeight = scroller.clientHeight - 12;
      const thumbHeight = Math.max(32, Math.round((scroller.clientHeight / scroller.scrollHeight) * trackHeight));
      const thumbTop = Math.round((scroller.scrollTop / overflow) * (trackHeight - thumbHeight));
      bar.style.top = `${Math.round(trackTop)}px`;
      bar.style.height = `${trackHeight}px`;
      const thumb = bar.firstElementChild;
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${thumbTop}px)`;
      bar.classList.add('is-visible');
    }

    _syncEmptyClass() {
      const isEmpty = !this.store.cart || this.store.cart.item_count === 0;
      this.classList.toggle('is-empty', isEmpty);
    }

    async _resync() {
      try {
        const [drawerHTML, bubbleHTML] = await Promise.all([
          this.api.fetchSectionHTML('cart-drawer'),
          this.api.fetchSectionHTML('cart-icon-bubble').catch(() => null),
        ]);
        this._beginPatch();
        this._patchDrawer(drawerHTML);
        if (bubbleHTML) this._patchBubble(bubbleHTML);
        this._readConfig();
        this.store.config = this.config;
        this._hydrateFromDOM();
        this._syncEmptyClass();
      } catch (error) {
        /* network down — keep current UI */
      }
    }

    _scheduleResync() {
      clearTimeout(this._resyncTimer);
      this._resyncTimer = setTimeout(() => {
        this.queue.enqueue(() => this._resync());
      }, 150);
    }

    _revalidateOnOpen() {
      if (this._revalidating) return;
      this._revalidating = true;
      this.queue.enqueue(async () => {
        try {
          const cart = await this.api.fetchCart();
          if (this.store.cart && cart.item_count === this.store.cart.item_count && cart.total_price === this.store.cart.total_price) {
            return;
          }
          this.store.commit(cart);
          await this._resync();
          this._enqueueRules();
        } catch (error) {
          /* silent */
        } finally {
          this._revalidating = false;
        }
      });
    }

    /* ------------------------------------------------------------ *
     * Errors, busy states, toasts, a11y
     * ------------------------------------------------------------ */
    _handleMutationError(error, lineKey) {
      this._mutationFailed = true;
      if (lineKey) this._setLinePending(lineKey, false);
      if (error && error.status && !error.isNetwork) {
        // Any 4xx/5xx with a response: bad quantity, stale line key, sold-out.
        // Data problem, not connectivity — resync and tell the shopper what
        // the server said, never "revisa tu conexión".
        const message = (error.payload && (error.payload.message || error.payload.description)) ||
          this.strings.cartError || 'No pudimos actualizar tu carrito';
        if (lineKey) this._setLineError(lineKey, message);
        else this._toast(message);
        this._scheduleResync();
      } else {
        this._toast(this.strings.networkError || 'No pudimos actualizar tu carrito. Revisa tu conexión.', {
          actionLabel: this.strings.retry || 'Reintentar',
          onAction: () => this._scheduleResync(),
        });
        this._scheduleResync();
      }
      if (window.publish && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartError) {
        publish(PUB_SUB_EVENTS.cartError, { source: 'hvm-cart-drawer', errors: error && error.message });
      }
      const coupon = this.querySelector('hvm-coupon-disclosure');
      if (coupon && typeof coupon.resetBusy === 'function') coupon.resetBusy();
      this._announce(this.strings.cartError || 'No pudimos actualizar tu carrito');
    }

    _setLineError(lineKey, message) {
      const container = this.querySelector(`[data-hvm-line-error="${CSS.escape(lineKey)}"]`);
      if (!container) return;
      container.hidden = false;
      const text = container.querySelector('[data-hvm-line-error-text]') || container;
      text.textContent = message;
    }

    _setCouponError(message) {
      const coupon = this.querySelector('hvm-coupon-disclosure');
      if (coupon && typeof coupon.showError === 'function') coupon.showError(message);
    }

    _setLinePending(lineKey, pending) {
      const line = this.querySelector(`[data-hvm-line="${CSS.escape(lineKey)}"]`);
      if (line) line.classList.toggle('is-pending', pending);
    }

    _setBusy(element, busy) {
      if (!element) return;
      element.toggleAttribute('disabled', busy);
      element.classList.toggle('is-busy', busy);
    }

    _offerUndo(item) {
      const payload = {
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || undefined,
      };
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        payload.selling_plan = item.selling_plan_allocation.selling_plan.id;
      }
      this._toast(
        (this.strings.itemRemoved || 'Eliminaste [title]').replace('[title]', item.product_title),
        {
          actionLabel: this.strings.undo || 'Deshacer',
          duration: 5000,
          onAction: () => this._enqueueAdd([payload], null),
        }
      );
    }

    _toast(message, options) {
      if (typeof HVMCart.toast === 'function') HVMCart.toast(this, message, options || {});
    }

    _announce(message) {
      const region = this.querySelector('#CartDrawer-LiveRegionText');
      if (!region) return;
      region.textContent = '';
      setTimeout(() => {
        region.textContent = message;
      }, 50);
    }

    _announceCart(cart) {
      this._announce(
        (this.strings.cartUpdated || 'Carrito actualizado. [n] productos. Total: [total]')
          .replace('[n]', String(cart.item_count))
          .replace('[total]', HVMCart.formatMoney(cart.total_price))
      );
    }

    /* ------------------------------------------------------------ *
     * Checkout — flush pending edits, wait for the queue, navigate once
     * ------------------------------------------------------------ */
    _onCheckout(event, button) {
      event.preventDefault();
      if (HVMCart.uiState.checkoutInFlight) return;
      HVMCart.uiState.checkoutInFlight = true;
      this._setBusy(button, true);
      button.classList.add('is-loading');
      this._mutationFailed = false;

      this.debouncer.flush();
      this.queue
        .idle()
        .then(() => {
          // A flushed edit that errored means the cart on the server is not
          // what the shopper sees — never navigate over that.
          if (this._mutationFailed) {
            HVMCart.uiState.checkoutInFlight = false;
            this._setBusy(button, false);
            button.classList.remove('is-loading');
            return;
          }
          window.location.href = button.dataset.checkoutUrl || '/checkout';
          setTimeout(() => {
            HVMCart.uiState.checkoutInFlight = false;
            this._setBusy(button, false);
            button.classList.remove('is-loading');
          }, 4000);
        });
    }
  }

  customElements.define('cart-drawer', HvmCartDrawer);
})();
